import { useMemo, useState } from 'react';
import { ArrowRight, Files, History, ScanText, Table2, Search } from 'lucide-react';
import type { OperatorCorrection, ProcessResult } from '../shared/desktop-api';
import { WorkspaceDialog } from './WorkspaceDialog';
import './corrections-panel.css';

function category(entry: OperatorCorrection) {
  const key = entry.kind.toLowerCase();
  if (key === 'document-confirmed') return 'Whole document';
  if (/line.item|table|row/.test(key)) return 'Line items';
  if (/document.type|classification/.test(key)) return 'Document type';
  // No "Page grouping": the engine's correction list (ListOperatorCorrections) holds no split or join.
  return 'Field value';
}
const icons = {
  'Whole document': Files,
  'Line items': Table2,
  'Document type': Files,
  'Field value': ScanText,
};
/** A confirmation, not a change: a verified kind, the whole document, or a row check (a line-item
 * correction that only verified the row, so the engine reports the same row text on both sides). */
function confirmation(entry: OperatorCorrection) {
  return (
    entry.kind.endsWith('-verified') ||
    entry.kind === 'document-confirmed' ||
    (entry.kind === 'line-item-correction' && entry.after != null && entry.before === entry.after)
  );
}
export function CorrectionsPanel({
  corrections,
  loading,
  failed = false,
  documents,
  onClose,
  onJump,
  onRetry,
  embedded = false,
  title = 'Review history',
  documentStatus = 'ready',
}: {
  corrections: OperatorCorrection[];
  loading: boolean;
  failed?: boolean;
  documents: ProcessResult['documents'];
  onJump?: (entry: OperatorCorrection) => void;
  onRetry?: () => void;
  title?: string;
  documentStatus?: 'loading' | 'ready' | 'error';
} & ({ embedded: true; onClose?: () => void } | { embedded?: false; onClose: () => void })) {
  const [query, setQuery] = useState('');
  const [type, setType] = useState('');
  const [kind, setKind] = useState('');
  const [range, setRange] = useState('all');
  const sources = useMemo(
    () => new Map(documents.map((doc, index) => [doc.id, { doc, index }])),
    [documents],
  );
  const typeName = (entry: OperatorCorrection) =>
    sources.get(entry.documentId)?.doc.documentType ||
    (documentStatus === 'ready' ? 'Unavailable document' : 'Document details unavailable');
  const types = [...new Set(corrections.map(typeName))].sort();
  const cutoff =
    range === 'all'
      ? 0
      : range === 'today'
        ? new Date().setHours(0, 0, 0, 0)
        : Date.now() - 7 * 86400000;
  const filtered = corrections
    .filter(
      (entry) =>
        (!type || typeName(entry) === type) &&
        (!kind || category(entry) === kind) &&
        (!cutoff || Date.parse(entry.occurredAt) >= cutoff) &&
        [
          entry.fieldName,
          entry.before,
          entry.after,
          typeName(entry),
          sources.get(entry.documentId)?.doc.sourceName,
        ].some((value) => value?.toLowerCase().includes(query.trim().toLowerCase())),
    )
    .sort((a, b) => Date.parse(b.occurredAt) - Date.parse(a.occurredAt));
  // Contiguous groups retain the newest-first timeline. Grouping all entries
  // of a type together can put an older invoice above a newer W-9 action.
  const groups: [string, OperatorCorrection[]][] = [];
  for (const entry of filtered) {
    const name = typeName(entry),
      previous = groups.at(-1);
    if (previous?.[0] === name) previous[1].push(entry);
    else groups.push([name, [entry]]);
  }
  const reset = () => {
    setQuery('');
    setType('');
    setKind('');
    setRange('all');
  };
  const content = (
    <div className={`correction-history${embedded ? ' is-embedded' : ''}`} aria-busy={loading}>
      <div className="history-filters">
        <label className="history-search">
          <Search size={19} />
          <input
            aria-label="Search corrections"
            placeholder="Search corrections"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <select
          aria-label="Document type filter"
          value={type}
          onChange={(e) => setType(e.target.value)}
        >
          <option value="">All document types</option>
          {types.map((name) => (
            <option key={name}>{name}</option>
          ))}
        </select>
        <select aria-label="Change filter" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">All changes</option>
          {Object.keys(icons).map((name) => (
            <option key={name}>{name}</option>
          ))}
        </select>
        <select aria-label="Date filter" value={range} onChange={(e) => setRange(e.target.value)}>
          <option value="all">Any time</option>
          <option value="today">Today</option>
          <option value="week">Last 7 days</option>
        </select>
        {(query || type || kind || range !== 'all') && (
          <button className="quiet-button" onClick={reset}>
            Clear filters
          </button>
        )}
      </div>
      <div className="history-summary">
        <History size={18} />
        <span role="status">
          {loading
            ? 'Loading history…'
            : failed
              ? 'Could not load history'
              : `${filtered.length} saved ${filtered.length === 1 ? 'action' : 'actions'}`}
        </span>
        <small>
          Newest first
          {onJump && documentStatus === 'ready' && sources.size > 0
            ? ' · Select an action to revisit its document'
            : ''}
        </small>
      </div>
      <div className="history-list">
        {failed ? (
          <div className="history-empty">
            <p>Your history couldn’t be loaded.</p>
            {onRetry && (
              <button className="quiet-button" onClick={onRetry}>
                Try again
              </button>
            )}
          </div>
        ) : loading ? null : !filtered.length ? (
          <div className="history-empty">
            <History size={40} />
            <h3>{corrections.length ? 'No matching actions' : 'Your teaching starts here'}</h3>
            <p>
              {corrections.length
                ? 'Try another filter or search.'
                : 'Confirmations and corrections you save will appear here.'}
            </p>
          </div>
        ) : (
          [...groups].map(([name, entries]) => {
            const mark = [...name].reduce((sum, c) => sum + c.charCodeAt(0), 0) % 4;
            return (
              <section className="history-group" key={entries[0].eventId}>
                <h3>
                  <span className={`history-type-mark mark-${mark}`}>
                    <Files size={22} />
                  </span>
                  {name}
                  <small>{entries.length}</small>
                </h3>
                {entries.map((entry) => {
                  const source = sources.get(entry.documentId);
                  const label = category(entry);
                  const Icon = icons[label];
                  const confirmed = confirmation(entry);
                  return (
                    <button
                      type="button"
                      className="history-entry"
                      key={entry.eventId}
                      disabled={!source || !onJump || documentStatus !== 'ready'}
                      onClick={() => onJump?.(entry)}
                    >
                      <div className="history-entry-meta">
                        <Icon size={23} />
                        <span>
                          <strong>{entry.fieldName || label}</strong>
                          <small>
                            {label} ·{' '}
                            {source
                              ? `Document ${source.index + 1}`
                              : documentStatus === 'loading'
                                ? 'Checking document availability…'
                                : documentStatus === 'error'
                                  ? 'Document availability could not be checked'
                                  : 'Document not available here'}
                          </small>
                        </span>
                        <time dateTime={entry.occurredAt}>
                          {new Date(entry.occurredAt).toLocaleString([], {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </time>
                      </div>
                      {confirmed ? (
                        <div className="history-values">
                          <div>
                            <small>Confirmed</small>
                            <span>{entry.after?.trim() || entry.fieldName}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="history-values">
                          <div>
                            <small>Before</small>
                            <span>
                              {entry.before == null
                                ? 'Not recorded'
                                : entry.before.trim() || 'Empty'}
                            </span>
                          </div>
                          <ArrowRight size={20} />
                          <div className="history-after">
                            <small>After</small>
                            <span>
                              {entry.after == null ? 'Not recorded' : entry.after.trim() || 'Empty'}
                            </span>
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </section>
            );
          })
        )}
      </div>
    </div>
  );
  return embedded ? (
    content
  ) : (
    <WorkspaceDialog telemetry="corrections" title={title} onClose={onClose!}>
      {content}
    </WorkspaceDialog>
  );
}
